import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Pointer-driven drag for the CRM pipeline board (ported from the Winning Team OS
 * pipeline, components/crm/board-drag.ts, where it fixed the same "dragging feels
 * buggy" report).
 *
 * The board used the native HTML5 drag API, and its faults were all in that API:
 * the browser's own translucent snapshot lags the cursor (and our own `.dragging`
 * style made it fainter still), `dragover` in the gaps between cards answered with
 * the column, so the drop line jumped to the bottom and back, and a column taller
 * than the board could not be scrolled to while dragging.
 *
 * Pointer events give a solid card under the hand, a slot that opens where the card
 * will land, edge auto-scroll for the row AND for each column's own card list, and
 * one gesture for mouse and pen. Touch keeps the card's Move menu (a finger on a
 * card scrolls the column).
 *
 * Geometry is measured ONCE when the drag begins, in coordinates that scrolling
 * cannot move: across the row in the board's scroll content, down a column in that
 * column's scroll content. Re-measuring every frame looks more correct and is
 * worse: the slot pushes the cards below it down, which moves the midpoint the slot
 * was chosen from, and the slot flickers between two places while the hand is still.
 */

export type DropTarget = { stageId: string; index: number };
export type CardOrigin = { dealId: number; stageId: string; index: number };
export type ActiveDrag = CardOrigin & { width: number; height: number; target: DropTarget; originX: number; originY: number };

export type ColumnGeometry = {
  stageId: string;
  /** In the board's scroll content, so sideways scrolling cannot invalidate them. */
  left: number;
  right: number;
  /** In this column's card-list scroll content. */
  cards: Array<{ top: number; bottom: number }>;
};

type Session = {
  pointerId: number;
  origin: CardOrigin;
  grabX: number; grabY: number;
  startX: number; startY: number;
  width: number; height: number;
  pointerX: number; pointerY: number;
  travel: number;
  started: boolean;
  columns: ColumnGeometry[];
  target: DropTarget;
  frame: number | null;
};

const START_THRESHOLD_PX = 5;
/** A press that drifted less than this and let go where it began is a click. */
const CLICK_SLOP_PX = 24;
const EDGE_X = 84;
const EDGE_Y = 56;
const MAX_SPEED = 22;
const MIN_SPEED = 4;
const speedFor = (gap: number, edge: number) => Math.max(MIN_SPEED, MAX_SPEED * (1 - Math.max(gap, 0) / edge));

const bodyOf = (col: Element) => col.querySelector<HTMLElement>("[data-col-body]") || (col as HTMLElement);

function measure(scroller: HTMLElement, excludeDealId: number): ColumnGeometry[] {
  const sr = scroller.getBoundingClientRect();
  return Array.from(scroller.querySelectorAll<HTMLElement>("[data-stage-id]")).map((col) => {
    const cr = col.getBoundingClientRect();
    const body = bodyOf(col);
    const br = body.getBoundingClientRect();
    const rows = Array.from(body.querySelectorAll<HTMLElement>("[data-deal-id]")).map((el) => ({ id: Number(el.dataset.dealId), rect: el.getBoundingClientRect() }));
    // Measured before React takes the dragged card out of its column, so the cards
    // below it are about to move up by its height plus the gap; bake that in.
    const goneAt = rows.findIndex((r) => r.id === excludeDealId);
    const shift = goneAt < 0 ? 0 : (rows[goneAt + 1]?.rect.top ?? rows[goneAt].rect.bottom + 8) - rows[goneAt].rect.top;
    const cards: ColumnGeometry["cards"] = [];
    rows.forEach((row, i) => {
      if (i === goneAt) return;
      const delta = goneAt >= 0 && i > goneAt ? shift : 0;
      cards.push({ top: row.rect.top - br.top + body.scrollTop - delta, bottom: row.rect.bottom - br.top + body.scrollTop - delta });
    });
    return { stageId: col.dataset.stageId || "", left: cr.left - sr.left + scroller.scrollLeft, right: cr.right - sr.left + scroller.scrollLeft, cards };
  });
}

/** The column nearest the pointer across the row, then the slot by card midpoints down it. Pure, for the tests. */
export function resolveTarget(columns: ColumnGeometry[], contentX: number, columnY: (stageId: string) => number, fallback: DropTarget): DropTarget {
  if (!columns.length) return fallback;
  const dx = (c: ColumnGeometry) => (contentX < c.left ? c.left - contentX : contentX > c.right ? contentX - c.right : 0);
  const column = columns.reduce((best, c) => (dx(c) < dx(best) ? c : best));
  const y = columnY(column.stageId);
  let index = column.cards.length;
  for (let i = 0; i < column.cards.length; i++) {
    if (y < (column.cards[i].top + column.cards[i].bottom) / 2) { index = i; break; }
  }
  return { stageId: column.stageId, index };
}

export function useBoardDrag({ scrollerRef, onDrop, onTap }: {
  scrollerRef: React.RefObject<HTMLDivElement | null>;
  onDrop: (origin: CardOrigin, target: DropTarget) => void;
  /** A press that barely moved and ended where it began: open the card. */
  onTap?: (origin: CardOrigin) => void;
}) {
  const [drag, setDrag] = useState<ActiveDrag | null>(null);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const session = useRef<Session | null>(null);
  const suppressClick = useRef(false);
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;
  const onTapRef = useRef(onTap);
  onTapRef.current = onTap;

  const stop = useCallback((commit: boolean) => {
    const s = session.current;
    session.current = null;
    if (!s) return;
    if (s.frame !== null) cancelAnimationFrame(s.frame);
    document.body.style.removeProperty("user-select");
    document.body.style.removeProperty("cursor");
    scrollerRef.current?.style.removeProperty("scroll-snap-type");
    if (s.started) {
      suppressClick.current = true;
      const moved = s.target.stageId !== s.origin.stageId || s.target.index !== s.origin.index;
      if (commit && moved) onDropRef.current(s.origin, s.target);
      // The card was swapped for a slot mid-gesture, so the browser never sends its
      // click: a press that drifted a few pixels and came back is a tap.
      else if (commit && s.travel <= CLICK_SLOP_PX) onTapRef.current?.(s.origin);
    }
    setDrag(null);
  }, [scrollerRef]);

  const frame = useCallback(() => {
    const s = session.current;
    const scroller = scrollerRef.current;
    if (!s || !scroller) return;
    const sr = scroller.getBoundingClientRect();
    // The row: more stages than a laptop is wide.
    const leftGap = s.pointerX - sr.left;
    const rightGap = sr.right - s.pointerX;
    if (leftGap < EDGE_X) scroller.scrollLeft -= speedFor(leftGap, EDGE_X);
    else if (rightGap < EDGE_X) scroller.scrollLeft += speedFor(rightGap, EDGE_X);

    const ghost = ghostRef.current;
    if (ghost) ghost.style.transform = `translate3d(${s.pointerX - s.grabX}px, ${s.pointerY - s.grabY}px, 0) rotate(1.5deg)`;

    const contentX = s.pointerX - sr.left + scroller.scrollLeft;
    const next = resolveTarget(s.columns, contentX, (stageId) => {
      const col = scroller.querySelector<HTMLElement>(`[data-stage-id="${CSS.escape(stageId)}"]`);
      if (!col) return 0;
      const body = bodyOf(col);
      const br = body.getBoundingClientRect();
      // The column's own list: a tall column scrolls under a pointer held near its ends.
      const topGap = s.pointerY - br.top;
      const bottomGap = br.bottom - s.pointerY;
      if (topGap < EDGE_Y && topGap > -EDGE_Y) body.scrollTop -= speedFor(topGap, EDGE_Y);
      else if (bottomGap < EDGE_Y && bottomGap > -EDGE_Y) body.scrollTop += speedFor(bottomGap, EDGE_Y);
      return s.pointerY - br.top + body.scrollTop;
    }, s.target);
    if (next.stageId !== s.target.stageId || next.index !== s.target.index) {
      s.target = next;
      // The only thing React hears about: once per slot change, not per pointer move.
      setDrag((d) => (d ? { ...d, target: next } : d));
    }
    s.frame = requestAnimationFrame(frame);
  }, [scrollerRef]);

  const begin = useCallback((s: Session) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    s.started = true;
    s.columns = measure(scroller, s.origin.dealId);
    document.body.style.setProperty("user-select", "none");
    document.body.style.setProperty("cursor", "grabbing");
    // Phones snap the row to a column; snapping would undo every auto-scroll step.
    scroller.style.setProperty("scroll-snap-type", "none");
    setDrag({ ...s.origin, width: s.width, height: s.height, target: s.target, originX: s.pointerX - s.grabX, originY: s.pointerY - s.grabY });
    s.frame = requestAnimationFrame(frame);
  }, [frame, scrollerRef]);

  useEffect(() => {
    function onMove(e: PointerEvent) {
      const s = session.current;
      if (!s || e.pointerId !== s.pointerId) return;
      s.pointerX = e.clientX;
      s.pointerY = e.clientY;
      s.travel = Math.max(s.travel, Math.hypot(e.clientX - s.startX, e.clientY - s.startY));
      if (!s.started) {
        if (s.travel < START_THRESHOLD_PX) return;
        begin(s);
      }
      e.preventDefault();
    }
    function onUp(e: PointerEvent) {
      if (session.current && e.pointerId !== session.current.pointerId) return;
      stop(true);
    }
    const onCancel = () => stop(false);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") stop(false); };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("blur", onCancel);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("blur", onCancel);
      window.removeEventListener("keydown", onKey);
      // Leaving mid-drag must not strand the page with no text selection.
      stop(false);
    };
  }, [begin, stop]);

  /** On the card's pointerdown. Left button, mouse or pen; never from a control inside the card. */
  const startDrag = useCallback((e: React.PointerEvent<HTMLElement>, origin: CardOrigin) => {
    if (session.current) return;
    // Every press starts fresh, so a drag can never swallow a later tap or click.
    suppressClick.current = false;
    if (e.button !== 0 || e.pointerType === "touch") return;
    if ((e.target as HTMLElement).closest("a,button,input,textarea,select,[role=menuitem],[data-no-drag]")) return;
    const card = (e.currentTarget as HTMLElement).closest<HTMLElement>("[data-deal-id]");
    if (!card) return;
    const rect = card.getBoundingClientRect();
    session.current = {
      pointerId: e.pointerId, origin,
      grabX: e.clientX - rect.left, grabY: e.clientY - rect.top,
      startX: e.clientX, startY: e.clientY, width: rect.width, height: rect.height,
      pointerX: e.clientX, pointerY: e.clientY, travel: 0, started: false,
      columns: [], target: { stageId: origin.stageId, index: origin.index }, frame: null,
    };
  }, []);

  /** True once after a drag, so the card's own click handler stands down. */
  const consumeClick = useCallback(() => {
    const suppressed = suppressClick.current;
    suppressClick.current = false;
    return suppressed;
  }, []);

  return { drag, ghostRef, startDrag, consumeClick };
}
